import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRightIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CreditCardIcon,
  LightningIcon,
} from '@phosphor-icons/react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import {
  assignCategoryColors,
  buildOccurrences,
  formatCurrency,
  formatDate,
  getCategoryColor,
  splitCategoryEmoji,
} from '@budget/core';
import type {
  Bill,
  BudgetCategory,
  BudgetItem,
  BudgetSummary,
  CategoryRow,
  DashboardData,
  Gift,
  Tri,
} from '@budget/core';
import { useBudgetActuals } from '../data/useBudgetActuals';
import { useDashboard } from '../data/useDashboard';
import { currentEtMonth, currentEtMonthKey } from '../lib/dates';
import { usePlaidItems } from '../data/usePlaidItems';
import { useTransactionsEnabled } from '../data/useTransactionsEnabled';
import { useHousehold } from '../auth/useHousehold';
import type { TransactionsScope } from '../data/useTransactions';
import { IconButton } from '../components/IconButton';
import { type Scope } from '../components/ScopeToggle';
import { useScope } from '../hooks/useScope';
import { PersonBreakdown } from '../components/PersonBreakdown';
import { BreakdownLegend } from '../components/BreakdownLegend';
import { BreakdownLegendGrid } from '../components/BreakdownLegendGrid';
import { DonutCenterEmoji } from '../components/DonutCenterEmoji';
import { CardHeader } from '../components/CardHeader';
import { CategoryProgressRing } from '../components/CategoryProgressRing';
import { getCategoryIcon } from '../components/CategoryIcon';
import { ProgressFill } from '../components/ProgressFill';
import { Tooltip } from '../components/Tooltip';
import { BluevineLogo, ChaseLogo, SoFiLogo } from '../components/ThirdPartyLogos';
import { TransactionsPage } from './TransactionsPage';
import { BillsPage } from './BillsPage';
import { getPersonName } from '../config/personNames';
import clsx from 'clsx';

const CHART_TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};
const CHART_TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };
const CHART_TOOLTIP_ITEM_STYLE = { color: 'var(--berg-text)' };

function fmt(n: number) {
  return formatCurrency(n);
}

function fmt0(n: number) {
  return formatCurrency(n, 'USD', 'en-US', 0);
}

function pickScope(t: Tri, scope: Scope): number {
  return t[scope];
}

// Strip every parenthetical from the displayed name on a category card's
// line items. The sheet uses parentheses for funding/account hints (e.g.
// "(SoFi Vault - HYSA)", "(Chase)", "(Auto)") that are either redundant
// with the funding indicator icon or just noise for the card view. Note:
// only the *displayed* name is trimmed — `item.name` keeps the parens
// elsewhere since it's the join key for actuals + rules.
function cleanItemName(name: string): string {
  return name.replace(/\s*\([^)]*\)/g, '').trim();
}

function FundingIndicator({ funding }: { funding: BudgetItem['funding'] }) {
  if (funding === 'vault') {
    return (
      <Tooltip content="Saved in a SoFi vault">
        <SoFiLogo size={16} />
      </Tooltip>
    );
  }
  if (funding === 'shared') {
    return (
      <Tooltip content="Paid from shared Chase account">
        <ChaseLogo size={16} />
      </Tooltip>
    );
  }
  if (funding === 'business') {
    return (
      <Tooltip content="Paid from Bluevine business account">
        <BluevineLogo size={16} />
      </Tooltip>
    );
  }
  if (funding === 'auto') {
    return (
      <Tooltip content="Automatically deducted from payroll">
        <LightningIcon size={16} weight="fill" className="text-fg-tertiary" />
      </Tooltip>
    );
  }
  // Fallback for items without an account-specific funding source — covers
  // the 'personal' case and anything else that isn't one of the named
  // accounts above. Keeps the icon slot occupied so line items align
  // consistently across the card.
  return (
    <Tooltip content="Personal spending">
      <CreditCardIcon size={16} weight="regular" className="text-fg-tertiary" />
    </Tooltip>
  );
}

// Small pill marking line items whose planned amount auto-counts as actual
// spending each month. Renders inline with the item name so the user can
// see at a glance which rows skip transaction reconciliation.
function AutoActualBadge() {
  return (
    <Tooltip content="Automatically deducted from payroll">
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-subtle text-fg-tertiary text-[10px] font-medium uppercase tracking-wide flex-none">
        Auto
      </span>
    </Tooltip>
  );
}

function HeroOutgoing({
  summary,
  categories,
  spendingByCategory,
  scope,
}: {
  summary: BudgetSummary;
  categories: BudgetCategory[];
  // Flat per-category planned-spending rows from the Spending tab. Used as
  // a fallback source for the broken-out segments — many Cash Out layouts
  // bundle Debt, Savings, and Retirement inside aggregate sections (or
  // inside the "Monthly Savings, Retirement, & Investments" summary block),
  // and the parser doesn't surface them as top-level BudgetCategory rows.
  // The Spending tab always lists them flat, so it's a reliable fallback.
  spendingByCategory: CategoryRow[];
  scope: Scope;
}) {
  // Cross-element hover sync between the segmented bar and the legend
  // rows: hovering either highlights the matching pair via this shared
  // key, and dims everything else to 0.2.
  const [hoveredBand, setHoveredBand] = useState<string | null>(null);
  const expenses = pickScope(summary.monthlyExpenses, scope);
  const outgoing = pickScope(summary.monthlyOutgoing, scope);

  // Break out Debt and Savings & Investments so the bar can show three
  // meaningful segments. Look in budget.categories first so colors and
  // totals line up with the donut when the Cash Out tab does surface them
  // as sections; if not, fall back to spending.byCategory. Names in the
  // sheet typically lead with an emoji (e.g. "💸 Debt"), so strip that
  // before comparing — the bare label is what we match on.
  //
  // The Savings & Investments segment also accepts the legacy parent names
  // ("Savings", "Investments & Insurance", "Retirement & Investments") and
  // sums them — so a partially-migrated sheet during the rename still
  // renders the full amount in one segment instead of dropping rows.
  const matchKey = (raw: string): string =>
    splitCategoryEmoji(raw).name.toLowerCase().trim();
  const lookup = (
    predicate: (key: string) => boolean,
  ): (Tri & { name: string }) | null =>
    categories.find((c) => predicate(matchKey(c.name))) ??
    spendingByCategory.find((c) => predicate(matchKey(c.name))) ??
    null;
  const debtCategory = lookup((k) => k === 'debt');
  const isSavingsAndInvestments = (k: string): boolean =>
    k.startsWith('savings') ||
    k.startsWith('investments') ||
    k.startsWith('retirement');
  const savingsAndInvestmentsCats = (() => {
    const fromBudget = categories.filter((c) =>
      isSavingsAndInvestments(matchKey(c.name)),
    );
    if (fromBudget.length > 0) return fromBudget;
    return spendingByCategory.filter((c) =>
      isSavingsAndInvestments(matchKey(c.name)),
    );
  })();
  const debt = debtCategory ? pickScope(debtCategory, scope) : 0;
  const savingsAndInvestments = savingsAndInvestmentsCats.reduce(
    (sum, c) => sum + pickScope(c, scope),
    0,
  );
  const expensesExDebt = Math.max(0, expenses - debt);

  // Each broken-out segment uses the same color the category renders in
  // everywhere else (donut, transactions badge, …) by looking it up in the
  // shared CATEGORY_COLORS map via assignCategoryColors. The leftover
  // "expenses" segment stays a neutral white so the colored slices read as
  // semantically meaningful against a non-colored backdrop.
  const colorMap = assignCategoryColors(categories.map((c) => c.name));
  const colorFor = (c: { name: string } | null, fallback: string): string =>
    (c && colorMap.get(c.name)) || fallback;

  const bands = [
    {
      key: 'expenses',
      label: 'Expenses',
      value: expensesExDebt,
      color: getCategoryColor('Expenses'),
    },
    {
      key: 'debt',
      label: 'Debt',
      value: debt,
      color: colorFor(debtCategory, getCategoryColor('Debt')),
    },
    {
      key: 'savings-investments',
      label: 'Savings & Investments',
      value: savingsAndInvestments,
      // Pick whichever matching category we found first to drive the color,
      // so the segment matches the shared category palette. Falls back to
      // amber (the merged category's fixed color in CATEGORY_COLORS).
      color: colorFor(savingsAndInvestmentsCats[0] ?? null, getCategoryColor('Savings & Investments')),
    },
  ];

  return (
    <div className="card flex flex-col gap-5 h-full">
      <div className="flex flex-col gap-1">
        <span className="hero-eyebrow">Monthly outgoing</span>
        <div className="hero-balance tabular leading-none">
          {fmt0(outgoing)}
          <span className="text-fg-tertiary text-base font-medium ml-2.5 tracking-normal">
            /mo
          </span>
        </div>
        <div className="mt-1">
          <PersonBreakdown
            dakota={summary.monthlyOutgoing.dakota}
            natalia={summary.monthlyOutgoing.natalia}
            digits={0}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-4">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-subtle gap-0.5">
          {bands.map((b) => {
            const pct = outgoing > 0 ? (b.value / outgoing) * 100 : 0;
            const dimmed = hoveredBand !== null && hoveredBand !== b.key;
            return (
              <ProgressFill
                key={b.key}
                pct={pct}
                color={b.color}
                style={{ opacity: dimmed ? 0.2 : 1 }}
                onMouseEnter={() => setHoveredBand(b.key)}
                onMouseLeave={() => setHoveredBand(null)}
              />
            );
          })}
        </div>
        <div className="hidden sm:block">
          <BreakdownLegendGrid
            items={bands.map((b) => ({
              key: b.key,
              label: b.label,
              value: b.value,
              color: b.color,
            }))}
            total={outgoing}
            formatValue={fmt0}
            hovered={hoveredBand}
            onHoverChange={setHoveredBand}
          />
        </div>
        <div className="sm:hidden">
          <BreakdownLegend
            items={bands.map((b) => ({
              key: b.key,
              label: b.label,
              value: b.value,
              color: b.color,
            }))}
            total={outgoing}
            formatValue={fmt0}
            hovered={hoveredBand}
            onHoverChange={setHoveredBand}
            collapseAfter={Infinity}
          />
        </div>
      </div>

    </div>
  );
}

// Standalone card that splits net monthly income into Outgoing + Remaining
// as a segmented bar — mirrors the Debts page debt-mix bar and the Assets
// page liquidity bar. When overspend the bar fills with outgoing only and
// the Remaining legend cell shows a negative red value.
function MonthlyRemainingCard({
  income,
  summary,
  spending,
  scope,
}: {
  income: DashboardData['income'];
  summary: BudgetSummary;
  spending: DashboardData['spending'];
  scope: Scope;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  // Falls back to net minus actual spending when the sheet's `remaining`
  // row is unset (early sheets before the formula was added).
  const r = income.remaining;
  const remainingTri =
    r.household !== 0 || r.dakota !== 0 || r.natalia !== 0
      ? r
      : {
          household: income.netMonthly.household - spending.totals.household,
          dakota: income.netMonthly.dakota - spending.totals.dakota,
          natalia: income.netMonthly.natalia - spending.totals.natalia,
        };
  const remaining = pickScope(remainingTri, scope);
  const outgoing = pickScope(summary.monthlyOutgoing, scope);
  const netIn = pickScope(income.netMonthly, scope);
  const overspend = remaining < 0;
  const remainingForBar = Math.max(0, remaining);
  const denom = Math.max(netIn, outgoing);
  const outgoingPct = denom > 0 ? (outgoing / denom) * 100 : 0;
  const remainingPct = denom > 0 ? (remainingForBar / denom) * 100 : 0;
  // "Remaining" carries the budget identity color when in the black; flips
  // to red as a semantic status when overspent.
  const remColor = overspend ? 'var(--berg-red)' : getCategoryColor('Remaining');
  const outColor = getCategoryColor('Outgoing');
  const KEYS = { out: 'out', left: 'left' } as const;

  return (
    <div className="card flex flex-col gap-5">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="hero-eyebrow">Monthly remaining</span>
          <div
            className={clsx(
              'hero-balance tabular leading-none',
              overspend && 'text-red',
            )}
          >
            {fmt0(remaining)}
            <span className="text-fg-tertiary text-base font-medium ml-2.5 tracking-normal">
              /mo
            </span>
          </div>
          <div className="mt-1">
            <PersonBreakdown
              dakota={remainingTri.dakota}
              natalia={remainingTri.natalia}
              digits={0}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-4">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-subtle gap-0.5">
          <ProgressFill
            pct={outgoingPct}
            color={outColor}
            style={{
              opacity: hovered !== null && hovered !== KEYS.out ? 0.2 : 1,
            }}
            onMouseEnter={() => setHovered(KEYS.out)}
            onMouseLeave={() => setHovered(null)}
          />
          {!overspend && (
            <ProgressFill
              pct={remainingPct}
              color={remColor}
              style={{
                opacity: hovered !== null && hovered !== KEYS.left ? 0.2 : 1,
              }}
              onMouseEnter={() => setHovered(KEYS.left)}
              onMouseLeave={() => setHovered(null)}
            />
          )}
        </div>
        <div className="hidden sm:block">
          <BreakdownLegendGrid
            items={[
              { key: KEYS.out, label: 'Outgoing', value: outgoing, color: outColor },
              {
                key: KEYS.left,
                label: 'Remaining',
                value: remaining,
                color: remColor,
                valueTone: overspend ? 'neg' : undefined,
              },
            ]}
            total={netIn}
            formatValue={fmt0}
            hovered={hovered}
            onHoverChange={(k) => setHovered(k as 'out' | 'left' | null)}
          />
        </div>
        <div className="sm:hidden">
          <BreakdownLegend
            items={[
              { key: KEYS.out, label: 'Outgoing', value: outgoing, color: outColor },
              {
                key: KEYS.left,
                label: 'Remaining',
                value: remaining,
                color: remColor,
                valueTone: overspend ? 'neg' : undefined,
              },
            ]}
            total={outgoing + Math.max(0, remaining)}
            formatValue={fmt0}
            hovered={hovered}
            onHoverChange={(k) => setHovered(k as 'out' | 'left' | null)}
            collapseAfter={Infinity}
          />
        </div>
      </div>
    </div>
  );
}

function CategoryCard({
  category,
  scope,
  totalForScope,
  actual,
  itemActuals,
  itemBudgetOverrides,
  uncategorizedCount,
  monthKey,
  categoryColor,
  expanded,
  onToggle,
}: {
  category: BudgetCategory;
  scope: Scope;
  totalForScope: number;
  // Actual spending for this category, in dollars. Undefined when no Plaid
  // data is available — the row falls back to planned-only in that case.
  actual?: number;
  // Per-item actual spending lookup (full sub-item name → amount).
  // Undefined when actuals aren't available.
  itemActuals?: Map<string, number>;
  // Per-item planned-amount override for the viewed month, sourced from
  // linked Bills. When set, replaces the sheet's averaged amount for both
  // the row and the category total so irregular items (annual domains,
  // quarterly water) show $0 in quiet months and the real expected total
  // in hit months. Stored as a Tri so household sub-columns stay
  // consistent with the scoped main number.
  itemBudgetOverrides?: Map<string, Tri>;
  // How many transactions are tagged at this parent category exactly (the
  // "Uncategorized" rollup). Drives the count badge on the Uncategorized
  // button. Falls back to 0 when actuals aren't loaded.
  uncategorizedCount?: number;
  // Which month the parent is showing actuals for — the Uncategorized
  // click passes this into the embedded transactions tab so the month
  // selector lines up with the data the card is summarizing.
  monthKey: string;
  categoryColor: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { emoji, name: bareName } = splitCategoryEmoji(category.name);
  const Icon = emoji ? null : getCategoryIcon(category.name);
  // When any item in this category has a bills-derived override, the
  // category total has to be re-summed so the header number matches the
  // sum of the rows below it. Categories without overrides keep using
  // the parser's pre-computed total to avoid any rounding drift.
  const hasItemOverrides =
    itemBudgetOverrides != null &&
    category.items.some((i) => itemBudgetOverrides.has(i.name));
  const value = hasItemOverrides
    ? category.items.reduce((sum, item) => {
        const override = itemBudgetOverrides!.get(item.name);
        return sum + (override ? pickScope(override, scope) : pickScope(item, scope));
      }, 0)
    : pickScope(category, scope);
  const pct = totalForScope > 0 ? (value / totalForScope) * 100 : 0;

  // Whether transactions are wired up for this card — gates the whole
  // "rows are clickable, click jumps to filtered transactions" pattern.
  // Without actuals there's no transactions sub-tab to send the user to,
  // so the rows revert to expand-only behavior.
  const transactionsEnabled = actual !== undefined;

  // Mirrors the Uncategorized button below: switch to the embedded
  // transactions tab, filter by the passed name, and sync the month so
  // the lists line up with the actuals the card is summarizing. Takes
  // the filter target as an argument so item rows can drill in by their
  // own raw `item.name` — that's what the transactions are tagged with
  // when auto-categorization lands them at the line-item level (e.g.
  // "Mortgage & Property" rather than the parent "Home & Pets").
  // Defaults to the parent's bare name, which is what the Uncategorized
  // rollup uses.
  const navigateToCategory = (target: string = bareName) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'transactions');
    next.set('category', target);
    next.set('month', monthKey);
    next.set('flash', '1');
    setSearchParams(next, { replace: true });
  };

  // The ring renders whenever actuals data is available for this scope —
  // even at $0 — so empty categories still show a track instead of going
  // ring-less and looking inconsistent next to spent siblings.
  const showRing = actual !== undefined;
  const hasSpending = actual !== undefined && actual > 0;
  // Over-budget is computed against the ROUNDED actual vs planned so it
  // matches what the user actually sees in the row (fmt0 rounds to whole
  // dollars). Without this, a $21.30 actual against a $21 plan both
  // display as "$21" but still flag red because the raw float is
  // strictly greater — looks like a bug to the user.
  const spentPct = hasSpending && value > 0 ? Math.min(100, (actual / value) * 100) : 0;
  const overBudget =
    hasSpending && value > 0 && Math.round(actual!) > Math.round(value);
  // Overage as a % of budget, capped at 100 so a 5x-overage doesn't lap the
  // ring and confuse the visual. The red overlay arc starts at 12-o'clock
  // and sweeps over the start of the (already-full) category-color ring.
  const overPct = overBudget ? Math.min(100, ((actual! - value) / value) * 100) : 0;

  // Hide line items that are zero in the current scope to keep things tight,
  // BUT keep zero-budget items that have recorded actuals — otherwise their
  // actual amount silently inflates the parent total without surfacing the
  // row, and users see a gap between the parent's "spent" number and the
  // visible items' actuals.
  const visibleItems = category.items.filter(
    (i) => pickScope(i, scope) !== 0 || (itemActuals?.get(i.name) ?? 0) > 0,
  );

  // Difference between the category-level actual and the sum of per-item
  // actuals — transactions tagged at the parent (e.g. Plaid PFC primary or
  // Apple CSV broad bucket) that never got pinned to a specific sub-item.
  // Surfacing this as its own row lets the visible numbers add up.
  const itemActualsTotal =
    itemActuals != null
      ? category.items.reduce(
          (sum, item) => sum + (itemActuals.get(item.name) ?? 0),
          0,
        )
      : 0;
  const uncategorized =
    itemActuals != null && actual !== undefined
      ? Math.max(0, actual - itemActualsTotal)
      : 0;
  const hasUncategorized = uncategorized > 0.01;

  return (
    <div className="card-flush">
      <button
        onClick={onToggle}
        className="w-full text-left hover:bg-overlay transition-colors"
      >
        <div className="flex items-center gap-3 sm:gap-4 px-4 py-3 sm:px-5 sm:py-4">
          <CategoryProgressRing
            emoji={emoji}
            Icon={Icon}
            spentPct={spentPct}
            overPct={overPct}
            color={categoryColor}
            showRing={showRing}
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{bareName}</div>
            <div className="text-xs text-fg-tertiary mt-0.5">
              {visibleItems.length} {visibleItems.length === 1 ? 'item' : 'items'} · {pct.toFixed(1)}%
            </div>
          </div>
          <div className="flex items-center gap-3 flex-none">
            <div className="flex flex-col items-end leading-tight">
              <span className="tabular font-semibold text-base">{fmt0(value)}</span>
              {actual !== undefined && (
                <span
                  className={clsx(
                    'tabular text-xs mt-0.5',
                    overBudget ? 'text-red' : 'text-fg-tertiary',
                  )}
                >
                  {overBudget ? (
                    <>{fmt0(actual - value)} over</>
                  ) : (
                    <>{fmt0(Math.max(0, value - actual))} left</>
                  )}
                </span>
              )}
            </div>
            <CaretDownIcon
              size={16}
              weight="bold"
              className={clsx(
                'text-fg-tertiary transition-transform',
                expanded && 'rotate-180'
              )}
            />
          </div>
        </div>
      </button>

      {expanded && (visibleItems.length > 0 || hasUncategorized) && (
        <div className="border-t border-border">
          {visibleItems.map((item) => {
            const itemOverride = itemBudgetOverrides?.get(item.name);
            const itemValue = itemOverride
              ? pickScope(itemOverride, scope)
              : pickScope(item, scope);
            const itemActual = itemActuals?.get(item.name);
            // TEMP DIAGNOSTIC: drop after Cloud Storage rounding is resolved.
            if (item.name.toLowerCase().includes('cloud')) {
              // eslint-disable-next-line no-console
              console.log(
                '[cloud-storage diag] ' +
                  JSON.stringify({
                    itemName: item.name,
                    scope,
                    rawSheet: {
                      household: item.household,
                      dakota: item.dakota,
                      natalia: item.natalia,
                    },
                    hasOverride: itemOverride != null,
                    override: itemOverride ?? null,
                    itemValueRaw: itemValue,
                    itemValueRounded: Math.round(itemValue),
                    itemActualRaw: itemActual ?? null,
                    itemActualRounded:
                      itemActual !== undefined ? Math.round(itemActual) : null,
                    diffRaw:
                      itemActual !== undefined ? itemActual - itemValue : null,
                    wouldFlagOverBudget:
                      itemActual !== undefined &&
                      itemValue > 0 &&
                      Math.round(itemActual) > Math.round(itemValue),
                  }),
              );
            }
            // Match the rounded display values when deciding the red
            // tint (see `overBudget` above for the same reasoning).
            const itemOverBudget =
              itemActual !== undefined &&
              itemValue > 0 &&
              Math.round(itemActual) > Math.round(itemValue);
            // Each item row filters by its own raw `item.name`, which is
            // the join key transactions get tagged with when auto-cat
            // lands them at the line-item level (e.g. "Mortgage &
            // Property" lives as its own value in budget_category).
            // Transactions tagged at the parent category level surface
            // through the Uncategorized rollup button below instead.
            const itemRowContent = (
              <>
                <div className="w-[22px] flex-none flex items-center justify-center">
                  <FundingIndicator funding={item.funding} />
                </div>
                <div className="flex-1 min-w-0 text-sm text-fg-secondary flex items-center gap-2">
                  <span className="truncate min-w-0">{cleanItemName(item.name)}</span>
                  {item.autoActual && <AutoActualBadge />}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  {scope === 'household' && (
                    <>
                      <span className="hidden sm:inline text-right tabular text-xs text-dakota w-20">
                        {fmt0(itemOverride?.dakota ?? item.dakota)}
                      </span>
                      <span className="hidden sm:inline text-right tabular text-xs text-natalia w-20">
                        {fmt0(itemOverride?.natalia ?? item.natalia)}
                      </span>
                    </>
                  )}
                  <span className="inline-flex items-baseline gap-1.5 tabular w-auto sm:w-24 justify-end">
                    {itemActual !== undefined && (
                      <span
                        className={clsx(
                          'text-xs',
                          itemOverBudget ? 'text-red' : 'text-fg-tertiary',
                        )}
                      >
                        {fmt0(itemActual)}
                      </span>
                    )}
                    <span className="font-medium">{fmt0(itemValue)}</span>
                  </span>
                </div>
              </>
            );
            return transactionsEnabled ? (
              <button
                key={item.name}
                type="button"
                onClick={() => navigateToCategory(item.name)}
                className="w-full flex items-center gap-3 sm:gap-4 px-4 py-2.5 sm:px-5 border-b border-border last:border-b-0 hover:bg-overlay transition-colors text-left"
              >
                {itemRowContent}
              </button>
            ) : (
              <div
                key={item.name}
                className="flex items-center gap-3 sm:gap-4 px-4 py-2.5 sm:px-5 border-b border-border last:border-b-0 hover:bg-overlay transition-colors"
              >
                {itemRowContent}
              </div>
            );
          })}
          {hasUncategorized && (
            <button
              type="button"
              onClick={() => {
                // Switch to the embedded transactions tab and pass the
                // parent category name so the list filters to rows tagged
                // at the parent level (the "uncategorized" rollup amount).
                // `categoryExact=1` keeps the transactions filter from
                // expanding to include line-items underneath this
                // parent — without it the rollup count and the visible
                // count diverge (the picker default surfaces every
                // child item too). `month` syncs the transactions view's
                // month selector with the actuals month the card is
                // summarizing. `flash` triggers the scroll-and-highlight.
                const next = new URLSearchParams(searchParams);
                next.set('tab', 'transactions');
                next.set('category', bareName);
                next.set('categoryExact', '1');
                next.set('month', monthKey);
                next.set('flash', '1');
                setSearchParams(next, { replace: true });
              }}
              className="w-full flex items-center gap-3 sm:gap-4 px-4 py-2.5 sm:px-5 border-b border-border last:border-b-0 bg-green-soft hover:bg-green-glow transition-colors text-left cursor-pointer"
            >
              {/* Count badge sits in the same `w-[22px]` slot the line
                  items use for their funding indicator so it lines up
                  with the rest of the column's leading icons. Shows the
                  number of transactions tagged at this parent category
                  exactly (the "Uncategorized" rollup). */}
              <div className="w-[22px] flex-none flex items-center justify-center">
                <span className="inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full border border-green bg-green-soft text-green text-[10px] font-semibold tabular leading-none">
                  {uncategorizedCount ?? ''}
                </span>
              </div>
              <div className="flex-1 min-w-0 text-sm text-green flex items-center gap-2">
                <span className="truncate min-w-0">Uncategorized</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="inline-flex items-baseline gap-1.5 tabular w-auto sm:w-24 justify-end">
                  <span className="text-xs text-green">{fmt0(uncategorized)}</span>
                </span>
                {/* Right arrow signals the row navigates to the filtered
                    transactions list (distinct from the chevron used on
                    inline expand toggles). */}
                <ArrowRightIcon
                  size={14}
                  weight="bold"
                  className="text-green flex-none"
                  aria-hidden
                />
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function BudgetBreakdownCard({
  categories,
  scope,
  actualTotal,
}: {
  categories: BudgetCategory[];
  scope: Scope;
  // Sum of actual spending across categories for the active scope. When
  // provided, surfaced as a smaller line under the budgeted total in the
  // header so the donut card mirrors the per-category "spent vs planned"
  // readout. Omit when actuals aren't available (no Plaid sync, etc).
  actualTotal?: number;
}) {
  const colorMap = useMemo(
    () => assignCategoryColors(categories.map((c) => c.name)),
    [categories]
  );

  // Keep zero-value categories in the legend but filter them out of the
  // donut itself — same pattern AssetBreakdownCard uses, so the legend lists
  // every category even when one zeroes out for the active scope.
  const breakdown = useMemo(
    () =>
      categories
        .map((c) => ({
          label: c.name,
          bareLabel: splitCategoryEmoji(c.name).name,
          value: pickScope(c, scope),
          household: c.household,
          color: colorMap.get(c.name) ?? 'var(--berg-neutral)',
        }))
        // Sort by the active scope's value so the legend reads largest-first
        // for whichever person is being viewed. Items the scope doesn't hit
        // fall to the bottom (still listed at $0).
        .sort((a, b) => b.value - a.value),
    [categories, scope, colorMap]
  );

  const pieData = useMemo(() => breakdown.filter((b) => b.value > 0), [breakdown]);
  const total = breakdown.reduce((sum, b) => sum + b.value, 0);
  const [hovered, setHovered] = useState<string | null>(null);

  // Compare rounded values so the over-budget styling agrees with the
  // whole-dollar numbers fmt0 puts on screen — see the line-item
  // overBudget in CategoryCard for the same reasoning.
  const overBudget =
    actualTotal !== undefined && Math.round(actualTotal) > Math.round(total);

  return (
    <div className="card">
      <CardHeader
        title="Breakdown"
        subtitle="By category"
        right={
          <div className="flex flex-col items-end leading-tight">
            <span className="tabular text-2xl font-semibold">{fmt0(total)}</span>
            {actualTotal !== undefined && (
              <span
                className={clsx(
                  'tabular text-xs mt-0.5',
                  overBudget ? 'text-red' : 'text-fg-tertiary',
                )}
              >
                {fmt0(actualTotal)} spent
              </span>
            )}
          </div>
        }
      />
      {pieData.length === 0 ? (
        <div className="empty-state py-8">No outflow in this scope.</div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="relative h-[220px] overflow-visible [&_.recharts-wrapper]:overflow-visible [&_svg]:overflow-visible">
            <DonutCenterEmoji
              emoji={hovered ? splitCategoryEmoji(hovered).emoji : null}
            />
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  animationDuration={1000}
                  nameKey="label"
                  innerRadius="60%"
                  outerRadius="98%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0.5}
                  stroke="var(--berg-bg-elevated)"
                  strokeWidth={1}
                  onMouseEnter={(_, idx) => setHovered(pieData[idx]?.label ?? null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {pieData.map((b) => {
                    const isFocused = hovered === null || hovered === b.label;
                    return (
                      <Cell
                        key={b.label}
                        fill={b.color}
                        fillOpacity={isFocused ? 1 : 0.2}
                        style={{ transition: 'fill-opacity 0.22s ease' }}
                      />
                    );
                  })}
                </Pie>
                <RechartsTooltip
                  formatter={(v) => fmt0(typeof v === 'number' ? v : 0)}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                  cursor={{ fill: 'transparent' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <BreakdownLegend
            items={breakdown.map((b) => ({
              key: b.label,
              label: b.bareLabel,
              value: b.value,
              color: b.color,
            }))}
            total={total}
            formatValue={fmt0}
            hovered={hovered}
            onHoverChange={setHovered}
          />
        </div>
      )}
    </div>
  );
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function GiftCard({
  gift,
  scope,
  isCurrentMonth,
  isNextMonth,
}: {
  gift: Gift;
  scope: Scope;
  isCurrentMonth: boolean;
  isNextMonth: boolean;
}) {
  const amount = pickScope(gift, scope);
  const { emoji, name: bareName } = splitCategoryEmoji(gift.name);
  const showSplit =
    scope === 'household' && (gift.dakota > 0 || gift.natalia > 0);
  const monthLabel =
    gift.month === null ? 'Unscheduled' : MONTH_NAMES[gift.month - 1];
  return (
    <div className="card-flush flex flex-col items-center text-center px-4 py-5 gap-2 hover:bg-overlay transition-colors">
      <div className="inline-flex items-center gap-1.5">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.625rem] uppercase tracking-[0.08em] font-semibold bg-overlay text-fg-secondary">
          {monthLabel}
        </span>
        {isCurrentMonth && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.625rem] uppercase tracking-[0.08em] font-semibold bg-green-soft text-green">
            Now
          </span>
        )}
        {isNextMonth && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.625rem] uppercase tracking-[0.08em] font-semibold bg-yellow-soft text-yellow">
            Soon
          </span>
        )}
      </div>
      <span className="text-5xl leading-none my-1" aria-hidden>
        {emoji ?? '🎁'}
      </span>
      <div
        className="text-xs sm:text-sm font-medium text-fg-secondary truncate w-full"
        title={gift.recipient ? `${gift.recipient} ${bareName}` : bareName}
      >
        {gift.recipient ? `${gift.recipient} ${bareName}` : bareName}
      </div>
      <div className="tabular text-xl sm:text-2xl font-semibold leading-none">
        {fmt0(amount)}
      </div>
      {showSplit && (
        <div className="mt-1 flex items-center justify-center gap-3 text-[0.6875rem] text-fg-tertiary tabular flex-wrap">
          {gift.dakota > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="person-dot dot-dakota" />
              {fmt0(gift.dakota)}
            </span>
          )}
          {gift.natalia > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="person-dot dot-natalia" />
              {fmt0(gift.natalia)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function GiftsView({ gifts, scope }: { gifts: Gift[]; scope: Scope }) {
  const visibleGifts = useMemo(
    () => gifts.filter((g) => pickScope(g, scope) > 0),
    [gifts, scope],
  );

  const currentMonth = currentEtMonth();
  const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;

  const orderedGifts = useMemo(() => {
    return visibleGifts
      .filter((g) => g.month === null || g.month >= currentMonth)
      .slice()
      .sort((a, b) => {
        if (a.month === b.month) return 0;
        if (a.month === null) return 1;
        if (b.month === null) return -1;
        return a.month - b.month;
      });
  }, [visibleGifts, currentMonth]);

  const totalAnnual = useMemo(
    () => visibleGifts.reduce((sum, g) => sum + pickScope(g, scope), 0),
    [visibleGifts, scope],
  );

  const totalsByPerson = useMemo(() => {
    let dakota = 0;
    let natalia = 0;
    for (const g of gifts) {
      dakota += g.dakota;
      natalia += g.natalia;
    }
    return { dakota, natalia };
  }, [gifts]);

  if (gifts.length === 0) {
    return (
      <div className="card empty-state">
        No gifts in the sheet yet — add a row to the Gifts tab with a name,
        month, and amount in the household, dakota, or natalia column.
      </div>
    );
  }

  return (
    <>
      <div className="card flex items-end justify-between gap-6 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="hero-eyebrow">
            {scope === 'household'
              ? 'Annual gift budget'
              : `${getPersonName(scope)} gift budget`}
          </span>
          <div className="hero-balance tabular leading-none">{fmt0(totalAnnual)}</div>
        </div>
        {scope === 'household' && (
          <PersonBreakdown
            dakota={totalsByPerson.dakota}
            natalia={totalsByPerson.natalia}
            digits={0}
          />
        )}
      </div>

      {orderedGifts.length === 0 ? (
        <div className="card empty-state">
          No upcoming gifts funded by{' '}
          {scope === 'household' ? 'the household' : getPersonName(scope)}.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3 max-xl:grid-cols-3 max-lg:grid-cols-2">
          {orderedGifts.map((g, i) => (
            <GiftCard
              key={`${g.name}-${i}`}
              gift={g}
              scope={scope}
              isCurrentMonth={g.month === currentMonth}
              isNextMonth={g.month === nextMonth}
            />
          ))}
        </div>
      )}
    </>
  );
}

type BudgetView = 'budget' | 'bills' | 'transactions' | 'gifts';

// 'YYYY-MM' month-key helpers for the actuals month switcher. Anchored
// to Eastern Time so a late-evening "today" never flips the month for a
// browser running in UTC or further east.
function shiftMonthKey(key: string, delta: number): string {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, (m ?? 1) - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return formatDate(new Date(Date.UTC(y, (m ?? 1) - 1, 15)), {
    month: 'long',
    year: 'numeric',
  });
}

export function BudgetPage() {
  const { data, isLoading, error } = useDashboard();
  const plaid = usePlaidItems();
  const household = useHousehold();
  const { scope } = useScope();
  // Map the page-level Scope into the API's TransactionsScope. `undefined`
  // means "default to the caller" — used for both the household scope and
  // while a person's user_id is still resolving.
  const actualsScope = useMemo<TransactionsScope | undefined>(() => {
    if (scope === 'household') return undefined;
    const viewAsUserId = scope === 'dakota' ? household.ids.dakota : household.ids.natalia;
    if (viewAsUserId == null) return undefined;
    return { viewAsUserId };
  }, [scope, household.ids]);
  // Month switcher for actuals. The default (no `actualsMonth` arg) lets
  // the API serve the current calendar month — matches the previous
  // behavior. When the user navigates back, we pass an explicit month-key.
  const todayMonthKey = useMemo(() => currentEtMonthKey(), []);
  const [actualsMonthKey, setActualsMonthKey] = useState<string>(todayMonthKey);
  const isCurrentActualsMonth = actualsMonthKey === todayMonthKey;
  const actuals = useBudgetActuals(
    actualsScope,
    isCurrentActualsMonth ? undefined : actualsMonthKey,
  );
  const { enabled: transactionsEnabled } = useTransactionsEnabled();
  // Rings (with empty arcs for zero-spend categories) only render when
  // there's a Plaid connection to compare against — otherwise they'd be
  // 0/$X forever and read as broken. Also gated by the admin toggle so
  // the partial Apple Card story doesn't reach the UI for now.
  //
  // Personal scopes require the household user_ids to be resolved so the
  // viewAs query param can be set.
  const showActuals =
    transactionsEnabled &&
    plaid.status === 'ready' &&
    plaid.householdHasItems &&
    (scope === 'household' ||
      (scope === 'dakota' && household.ids.dakota != null) ||
      (scope === 'natalia' && household.ids.natalia != null));
  // Tab is URL-driven so external links can deep-link to a tab.
  // Categories are URL-driven too: the dashboard's progress tiles link to
  // /budget?cat=<bare-name> to auto-expand that category on arrival.
  // The legacy ?tab=remaining (its content was folded into the Overview
  // summary card) silently falls back to 'budget' here.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const catParam = searchParams.get('cat');
  const view: BudgetView =
    tabParam === 'transactions' && transactionsEnabled
      ? 'transactions'
      : tabParam === 'gifts' || tabParam === 'bills'
        ? tabParam
        : 'budget';
  const setView = (next: BudgetView) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'budget') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());

  const budget = data?.budget;

  // Auto-expand the category named in `?cat=`, then clear the param so a
  // manual collapse sticks across data refreshes (otherwise the next
  // refresh would re-expand it).
  useEffect(() => {
    if (!catParam || !budget) return;
    const match = budget.categories.find(
      (c) => splitCategoryEmoji(c.name).name === catParam,
    );
    if (!match) return;
    setExpandedSet((prev) => {
      if (prev.has(match.name)) return prev;
      const next = new Set(prev);
      next.add(match.name);
      return next;
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('cat');
        return next;
      },
      { replace: true },
    );
  }, [catParam, budget, setSearchParams]);

  // Sort by household amount (not the scoped value) so the card order is
  // locked across scope changes — switching to Dakota or Natalia hides any
  // card where that person has $0, but the remaining cards stay in the same
  // big-to-small household order instead of shuffling per scope.
  const visibleCategories = useMemo(() => {
    if (!budget) return [];
    return [...budget.categories]
      .filter((c) => pickScope(c, scope) !== 0)
      .sort((a, b) => b.household - a.household);
  }, [budget, scope]);

  // Shared color assignment so the row's progress ring matches the donut
  // legend and any other category-colored surface on the page.
  const categoryColors = useMemo(
    () => assignCategoryColors(budget?.categories.map((c) => c.name) ?? []),
    [budget],
  );

  // Roll the raw per-budget_category totals up to the bare top-level name
  // each category lives under. A txn categorized as the sub-item "Mortgage"
  // should count toward "Home" (matching the parent-category badge the
  // Transactions page renders) — without this rollup, item-level txns get
  // dropped from the Budget actuals entirely.
  //
  // Auto-actual items (green-flagged rows in the sheet — 401k contributions,
  // payroll-deducted HSA, etc.) get their planned amount layered on top
  // since they don't produce a Plaid transaction. The category's bar reads
  // as fully spent for those items without manual reconciliation.
  const actualsByCategory = useMemo(() => {
    const result = new Map<string, number>();
    if (!budget) return result;
    const itemToParent = new Map<string, string>();
    const knownBareNames = new Set<string>();
    for (const c of budget.categories) {
      const bareCat = splitCategoryEmoji(c.name).name;
      knownBareNames.add(bareCat);
      for (const item of c.items) {
        if (item.name) itemToParent.set(item.name, bareCat);
      }
    }
    for (const [stored, amount] of Object.entries(actuals.totals)) {
      const bareStored = splitCategoryEmoji(stored).name;
      const parent = knownBareNames.has(bareStored)
        ? bareStored
        : itemToParent.get(stored) ?? itemToParent.get(bareStored);
      if (!parent) continue;
      result.set(parent, (result.get(parent) ?? 0) + amount);
    }
    for (const c of budget.categories) {
      const bareCat = splitCategoryEmoji(c.name).name;
      for (const item of c.items) {
        if (!item.autoActual) continue;
        const planned = pickScope(item, scope);
        if (planned === 0) continue;
        result.set(bareCat, (result.get(bareCat) ?? 0) + planned);
      }
    }
    return result;
  }, [budget, actuals.totals, scope]);

  // Per-category count of transactions tagged AT the parent level (i.e.
  // the "Uncategorized" rollup amount on the card's Uncategorized button).
  // We only count rows where the stored budget_category matches a parent
  // bare-name — anything stored as a sub-item ("Rent", "Vet Bills") is
  // categorized and doesn't belong in this count.
  const uncategorizedCountsByCategory = useMemo(() => {
    const result = new Map<string, number>();
    if (!budget) return result;
    const knownBareNames = new Set<string>();
    for (const c of budget.categories) {
      knownBareNames.add(splitCategoryEmoji(c.name).name);
    }
    for (const [stored, count] of Object.entries(actuals.counts)) {
      const bareStored = splitCategoryEmoji(stored).name;
      if (!knownBareNames.has(bareStored)) continue;
      result.set(bareStored, (result.get(bareStored) ?? 0) + count);
    }
    return result;
  }, [budget, actuals.counts]);

  // Per-item actuals: looked up by exact stored budget_category. Only
  // transactions explicitly tagged to a sub-item land here — parent-level
  // auto-categorizations (Plaid PFC primary, Apple CSV broad buckets) get
  // counted in the parent rollup above but can't be attributed to a
  // specific item. Both the bare key and the canonical item name (with
  // parens) are populated so callers can look up either form. Auto-actual
  // items get their planned amount as their actual, same logic as the
  // category rollup above.
  const actualsByItem = useMemo(() => {
    const result = new Map<string, number>();
    if (!budget) return result;
    const knownItemNames = new Set<string>();
    for (const c of budget.categories) {
      for (const item of c.items) {
        if (item.name) knownItemNames.add(item.name);
      }
    }
    for (const [stored, amount] of Object.entries(actuals.totals)) {
      if (knownItemNames.has(stored)) {
        result.set(stored, (result.get(stored) ?? 0) + amount);
      }
    }
    for (const c of budget.categories) {
      for (const item of c.items) {
        if (!item.autoActual || !item.name) continue;
        const planned = pickScope(item, scope);
        if (planned === 0) continue;
        result.set(item.name, (result.get(item.name) ?? 0) + planned);
      }
    }
    return result;
  }, [budget, actuals.totals, scope]);

  // For Cash Out items linked to one or more Bills (via the bill's
  // `cashOutItem` column), override the displayed monthly amount with
  // the sum of bill occurrences falling in the viewed month. Lets
  // irregularly-billed items (annual domains, quarterly water) compare
  // actuals against the real expected for THIS month instead of the
  // averaged value from the Cash Out sheet.
  //
  // Per-person slices mirror BillsPage's filter+rescale: sole-
  // responsibility bills (responsible === 'dakota' | 'natalia') go in
  // full on that person's scope and are excluded from the other's;
  // shared bills (responsible === 'shared') are scaled by the Cash Out
  // item's split ratio. Without this filter, Natalia-only domains would
  // leak into Dakota's view at half-value and vice versa.
  const itemBudgetOverrides = useMemo(() => {
    const result = new Map<string, Tri>();
    if (!budget) return result;
    const allBills = data?.bills.items ?? [];
    if (allBills.length === 0) return result;

    const billsByItem = new Map<string, Bill[]>();
    for (const bill of allBills) {
      if (!bill.cashOutItem) continue;
      const list = billsByItem.get(bill.cashOutItem);
      if (list) list.push(bill);
      else billsByItem.set(bill.cashOutItem, [bill]);
    }
    if (billsByItem.size === 0) return result;

    const [yStr, mStr] = actualsMonthKey.split('-');
    const y = Number(yStr);
    const m = Number(mStr);
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m, 0);

    for (const c of budget.categories) {
      for (const item of c.items) {
        const linkedBills = billsByItem.get(item.name);
        if (!linkedBills) continue;

        // Zero-household items can't derive a ratio — fall back to
        // surfacing shared bills at full amount on every scope, matching
        // BillsPage's "better to over-count than silently hide" choice.
        const hasRatios = item.household > 0;
        const dakotaRatio = hasRatios ? item.dakota / item.household : 1;
        const nataliaRatio = hasRatios ? item.natalia / item.household : 1;

        let household = 0;
        let dakota = 0;
        let natalia = 0;
        for (const occ of buildOccurrences(linkedBills, monthStart, monthEnd)) {
          const amount = occ.bill.amount;
          household += amount;
          switch (occ.bill.responsible) {
            case 'dakota':
              dakota += amount;
              break;
            case 'natalia':
              natalia += amount;
              break;
            case 'shared':
              dakota += amount * dakotaRatio;
              natalia += amount * nataliaRatio;
              break;
          }
        }
        // Only override when the bill actually occurred this month. Yearly /
        // quarterly / semi-annual bills produce no occurrences in 11 (or 9,
        // or 11) months out of 12 — without this guard the override clobbers
        // the smoothed monthly Cash Out amount (e.g. $14.50/mo for Jewelry
        // Insurance) with $0 every non-bill month, hiding the budget from
        // both the user and any per-person attribution math.
        if (household > 0 || dakota > 0 || natalia > 0) {
          result.set(item.name, { household, dakota, natalia });
        }
      }
    }
    return result;
  }, [budget, data?.bills, actualsMonthKey]);

  const toggle = (name: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const outgoingForScope = budget ? pickScope(budget.summary.monthlyOutgoing, scope) : 0;

  return (
    <div className="page-container space-y-5">
      <header>
        <div className="page-header">
          <div>
            <h1 className="page-title">Spending</h1>
            <p className="page-subtitle">
              {view === 'budget'
                ? 'Planned monthly outgoing, by category'
                : view === 'bills'
                  ? 'Recurring bills and one-time payments by due date'
                  : view === 'gifts'
                    ? 'Budgeted gifts for the rest of the year'
                    : 'Activity from your connected accounts'}
            </p>
          </div>
        </div>
      </header>
      <div className="tab-row max-md:tab-row-sticky max-md:-mt-5">
          <div className="page-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={view === 'budget'}
              onClick={() => setView('budget')}
              className={clsx('page-tab', view === 'budget' && 'page-tab-active')}
            >
              Budget
            </button>
            <button
              role="tab"
              aria-selected={view === 'bills'}
              onClick={() => setView('bills')}
              className={clsx('page-tab', view === 'bills' && 'page-tab-active')}
            >
              Bills
            </button>
            <button
              role="tab"
              aria-selected={view === 'gifts'}
              onClick={() => setView('gifts')}
              className={clsx('page-tab', view === 'gifts' && 'page-tab-active')}
            >
              Gifts
            </button>
            {transactionsEnabled && (
              <button
                role="tab"
                aria-selected={view === 'transactions'}
                onClick={() => setView('transactions')}
                className={clsx(
                  'page-tab',
                  view === 'transactions' && 'page-tab-active',
                )}
              >
                Transactions
              </button>
            )}
          </div>
      </div>

      {error && <div className="alert-error">{error}</div>}

      {!data && isLoading && (
        <div className="card text-center py-12 text-fg-secondary">Loading…</div>
      )}

      {budget && view === 'budget' && (
        <>
          <div className="grid grid-cols-3 gap-5 max-md:grid-cols-1">
            <div className="col-span-2 max-md:col-span-1 flex flex-col gap-5">
              <HeroOutgoing
                summary={budget.summary}
                categories={budget.categories}
                spendingByCategory={data.spending.byCategory}
                scope={scope}
              />
              <MonthlyRemainingCard
                income={data.income}
                summary={budget.summary}
                spending={data.spending}
                scope={scope}
              />
            </div>
            <BudgetBreakdownCard
              categories={budget.categories}
              scope={scope}
              actualTotal={
                showActuals
                  ? Array.from(actualsByCategory.values()).reduce(
                      (sum, n) => sum + n,
                      0,
                    )
                  : undefined
              }
            />
          </div>

          {showActuals && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-lg font-semibold">{monthLabel(actualsMonthKey)}</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setActualsMonthKey(todayMonthKey)}
                  disabled={isCurrentActualsMonth}
                  aria-hidden={isCurrentActualsMonth}
                  tabIndex={isCurrentActualsMonth ? -1 : 0}
                  style={{ visibility: isCurrentActualsMonth ? 'hidden' : 'visible' }}
                  className="px-3 h-8 text-xs font-medium rounded-full border border-border-strong text-fg hover:bg-overlay transition-colors"
                >
                  Reset
                </button>
                <div className="flex items-center gap-1.5">
                  <IconButton
                    onClick={() => setActualsMonthKey(shiftMonthKey(actualsMonthKey, -1))}
                    aria-label="Previous month"
                    variant="outline"
                    icon={<CaretLeftIcon size={14} weight="bold" />}
                  />
                  <IconButton
                    onClick={() => setActualsMonthKey(shiftMonthKey(actualsMonthKey, 1))}
                    disabled={isCurrentActualsMonth}
                    aria-label="Next month"
                    variant="outline"
                    icon={<CaretRightIcon size={14} weight="bold" />}
                  />
                </div>
              </div>
            </div>
          )}

          {visibleCategories.length === 0 ? (
            <div className="card empty-state">
              No budgeted outflow for {scope === 'household' ? 'this household' : getPersonName(scope)}.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {visibleCategories.map((c) => (
                <CategoryCard
                  key={c.name}
                  category={c}
                  scope={scope}
                  totalForScope={outgoingForScope}
                  actual={
                    showActuals
                      ? actualsByCategory.get(splitCategoryEmoji(c.name).name) ?? 0
                      : undefined
                  }
                  itemActuals={showActuals ? actualsByItem : undefined}
                  itemBudgetOverrides={itemBudgetOverrides}
                  uncategorizedCount={
                    showActuals
                      ? uncategorizedCountsByCategory.get(splitCategoryEmoji(c.name).name)
                      : undefined
                  }
                  monthKey={actualsMonthKey}
                  categoryColor={categoryColors.get(c.name) ?? 'var(--berg-neutral)'}
                  expanded={expandedSet.has(c.name)}
                  onToggle={() => toggle(c.name)}
                />
              ))}
            </div>
          )}

          {/* Annualized footnote */}
          <div className="text-xs text-fg-tertiary text-center pt-2">
            Annualized: <span className="tabular text-fg-secondary">{fmt(pickScope(budget.summary.annualOutgoing, scope))}</span>
          </div>
        </>
      )}

      {view === 'bills' && <BillsPage embedded />}

      {data && view === 'gifts' && (
        <GiftsView gifts={data.gifts.items} scope={scope} />
      )}

      {view === 'transactions' && <TransactionsPage embedded />}
    </div>
  );
}
